import { useState } from 'react';

import { Anchor, Button, List, Stack, Text } from '@mantine/core';

import { Modal } from '../Modal';

import { TERMS_OF_SERVICE_ACCEPTED_KEY } from '@/utils/constant';
import { TERMS_OF_SERVICE_DATA, TermsSection } from '@/utils/termsOfServiceData';

type TermsOfServiceModalProps = {
  opened: boolean;
  onClose: () => void;
  forceOpen?: boolean;
  hideAcceptButton?: boolean;
};

// URLを検出してリンク化する関数
const renderTextWithLinks = (text: string): React.ReactNode => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;

  // eslint-disable-next-line no-cond-assign
  let match = urlRegex.exec(text);
  while (match !== null) {
    // URLの前のテキスト
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }
    // URLをリンク化
    const url = match[0];
    key += 1;
    parts.push(
      <Anchor
        key={`link-${key}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        c="primary"
        sx={{
          color: '#0064be',
          textDecoration: 'underline',
        }}
      >
        {url}
      </Anchor>
    );
    lastIndex = match.index + match[0].length;
    // eslint-disable-next-line no-cond-assign
    match = urlRegex.exec(text);
  }

  // 残りのテキスト
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? <>{parts}</> : text;
};

const renderSection = (section: TermsSection, key: string): React.ReactNode => {
  switch (section.type) {
    case 'title':
      return (
        <Text key={key} fw={700} size="xl" mb="md" ta="center">
          {renderTextWithLinks(section.content)}
        </Text>
      );

    case 'section':
      return (
        <Stack key={key} spacing="sm" mb="lg">
          <Text fw={600} size={section.level === 0 ? 'md' : 'lg'}>
            {renderTextWithLinks(section.content)}
          </Text>
          {section.items?.map((item, idx) => renderSection(item, `${key}-${idx}`))}
        </Stack>
      );

    case 'subsection':
      return (
        <Stack key={key} spacing="xs" mb="md" pl="md">
          <Text fw={600} size="md">
            {renderTextWithLinks(section.content)}
          </Text>
          {section.items?.map((item, idx) => renderSection(item, `${key}-${idx}`))}
        </Stack>
      );

    case 'paragraph':
      return (
        <Text key={key} size="sm" mb="xs">
          {renderTextWithLinks(section.content)}
        </Text>
      );

    case 'list':
      return (
        <List key={key} size="sm" mb="md" pl="md" withPadding>
          {section.items?.map((item, idx) => renderSection(item, `${key}-${idx}`))}
        </List>
      );

    case 'listItem':
      return (
        <List.Item key={key} mb="xs">
          {renderTextWithLinks(section.content)}
        </List.Item>
      );

    default:
      return null;
  }
};

export const TermsOfServiceModal = ({
  opened,
  onClose,
  forceOpen = false,
  hideAcceptButton = false,
}: TermsOfServiceModalProps) => {
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);

  const handleAccept = () => {
    try {
      localStorage.setItem(TERMS_OF_SERVICE_ACCEPTED_KEY, 'true');
      onClose();
    } catch (error) {
      console.error('Failed to save terms acceptance:', error);
      // エラーが発生してもモーダルは閉じる（ユーザー体験を優先）
      onClose();
    }
  };

  const handleClose = () => {
    if (!forceOpen) {
      onClose();
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const { scrollTop } = element;
    const { scrollHeight } = element;
    const { clientHeight } = element;
    const threshold = 10; // 10pxの余裕を持たせる

    const reachedBottom = scrollTop + clientHeight >= scrollHeight - threshold;
    setIsScrolledToBottom(reachedBottom);
  };

  const isButtonDisabled = forceOpen && !isScrolledToBottom;

  return (
    <Modal
      size={700}
      opened={opened}
      onClose={handleClose}
      title="利用規約"
      closeOnClickOutside={!forceOpen}
      closeOnEscape={!forceOpen}
      hideCloseButton={forceOpen}
      onScrollContent={handleScroll}
      zIndex={1001}
      renderFooter={
        hideAcceptButton
          ? undefined
          : () => (
              <Button w={140} radius="xl" onClick={handleAccept} disabled={isButtonDisabled}>
                同意する
              </Button>
            )
      }
    >
      <Stack spacing="md">
        {TERMS_OF_SERVICE_DATA.map((section, idx) => renderSection(section, `section-${idx}`))}
      </Stack>
    </Modal>
  );
};
